"use client";

import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
type UseRevealOnScrollOptions = {
  once?: boolean;
  threshold?: number | number[];
  rootMargin?: string;
};
function useRevealOnScroll<T extends HTMLElement = HTMLElement>({
  once = true,
  threshold = 0.2,
  rootMargin = "0px"
}: UseRevealOnScrollOptions = {}) {
  const ref = useRef<T | null>(null);
  const [isVisible, setIsVisible] = useState(false);
  const useIsomorphicLayoutEffect = typeof window !== "undefined" ? useLayoutEffect : useEffect;
  useIsomorphicLayoutEffect(() => {
    const node = ref.current;
    if (!node || typeof window === "undefined") return;
    const rect = node.getBoundingClientRect();
    const alreadyVisible = rect.top < window.innerHeight && rect.bottom > 0 && rect.left < window.innerWidth && rect.right > 0;
    if (alreadyVisible) {
      setIsVisible(true);
    }
  }, []);
  useEffect(() => {
    const node = ref.current;
    if (!node || once && isVisible) return;
    if (typeof IntersectionObserver === "undefined") {
      setIsVisible(true);
      return;
    }
    const observer = new IntersectionObserver(entries => {
      const entry = entries[0];
      if (entry?.isIntersecting) {
        setIsVisible(true);
        if (once) observer.unobserve(entry.target);
      } else if (!once) {
        setIsVisible(false);
      }
    }, {
      threshold,
      rootMargin
    });
    observer.observe(node);
    return () => observer.disconnect();
  }, [once, rootMargin, threshold, isVisible]);
  return {
    ref,
    isVisible
  };
}
const processSteps = [{
  id: "01",
  title: "Select",
  description: "Browse our curated portfolio of premium serviced accommodations in prime locations, tailored for business and leisure."
}, {
  id: "02",
  title: "Secure",
  description: "Book instantly with flexible options and receive immediate confirmation, ensuring a hassle-free reservation process."
}, {
  id: "03",
  title: "Stay",
  description: "Experience the comfort of home with modern amenities and exceptional guest support throughout your entire visit."
}];
export default function OurSeamlessProcess() {
  const {
    ref,
    isVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.1
  });
  return <section id="our-seamless-process" className="dark bg-background text-foreground relative overflow-hidden py-24 md:py-44">
      <style dangerouslySetInnerHTML={{
      __html: `
          @keyframes ticker {
            0% { transform: translateX(0); }
            100% { transform: translateX(-50%); }
          }
          .animate-ticker {
            animation: ticker 40s linear infinite;
          }
        `
    }} />

      <div className="container mx-auto px-4 md:px-8">
        <div className="grid grid-cols-1 gap-16 md:grid-cols-12 md:gap-8">
          {}
          <div ref={ref} className="col-span-1 md:col-span-9 flex flex-col gap-24 md:gap-32">
            {processSteps.map((step, index) => <div key={step.id} data-index={index} className={`relative flex flex-col items-start gap-6 md:flex-row md:items-center md:gap-16 transition-all duration-800 ease-[cubic-bezier(0.16,1,0.3,1)] ${isVisible ? "translate-y-0 opacity-100" : "translate-y-[30px] opacity-0"}`} style={{
            transitionDelay: `${index * 100}ms`
          }}>
                {}
                <div className="pointer-events-none absolute -top-12 -left-4 z-0 select-none font-default text-[8rem] font-bold leading-none tracking-tighter text-primary/10 md:-top-20 md:-left-8 md:text-[14rem]">
                  {step.id}
                </div>

                {}
                <div className="relative z-10 flex w-full flex-col gap-4 md:flex-row md:items-center md:justify-between">
                  <h3 className="font-default text-4xl font-medium tracking-tight md:text-5xl">
                    {step.title}
                  </h3>
                  <p className="w-full text-lg font-light leading-relaxed tracking-wide text-muted-foreground md:w-[300px]">
                    {step.description}
                  </p>
                </div>
              </div>)}
          </div>

          {}
          <div className="hidden md:col-span-3 md:flex items-center justify-end border-l border-border/30 pl-8 relative">
            <div className="absolute top-0 left-0 h-full w-[1px] bg-gradient-to-b from-transparent via-border/50 to-transparent"></div>
            <div className="origin-center rotate-90 whitespace-nowrap font-mono text-xs font-semibold uppercase tracking-[0.15em] text-muted-foreground">
              Process Flow
            </div>
          </div>
        </div>
      </div>

      {}
      <div className="absolute bottom-0 left-0 flex w-full overflow-hidden border-t border-border/20 bg-background py-3">
        <div className="animate-ticker flex w-max min-w-full shrink-0 items-center gap-8">
          {[...Array(2)].map((_, groupIndex) => <div key={groupIndex} data-index={groupIndex} className="flex shrink-0 items-center gap-8">
              {[...Array(10)].map((_, i) => <span key={i} data-index={i} className="whitespace-nowrap font-mono text-[10px] font-semibold uppercase tracking-[0.15em] text-muted-foreground">
                  COMFORT • RELIABILITY • CLEANLINESS
                </span>)}
            </div>)}
        </div>
      </div>
    </section>;
}